/**
 * @file PsychosocialRiskPsycologyListView.jsx
 * @author Fernando Ruano
 * @description Psychosocial Risk Psycology List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function PsychosocialRiskPsycologyListView({
  dashboardStaffActions,
  clinicId
}) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { riskParam, month, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getPsychosocialRiskPsychologyListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('Última valoracion de psicología'),
      dataIndex: 'ultima_valoración_psicologia',
      filter: {
        listName: 'lista_ultima_valoracion_psicología',
        searchName: 'filtroUltimaValoracionPsicologia',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('ultima_valoración_psicologia'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_ultima_valoracion_psicología',
            searchName: 'filtroUltimaValoracionPsicologia'
          },
          dataIndex: 'ultima_valoración_psicologia'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('Último registro de riesgo psicosocial'),
      dataIndex: 'ultimo_registro_riesgo_psicosocial_psicología',
      filter: {
        listName: 'lista_ultimo_registro_riesgo_psicosocial',
        searchName: 'filtroUltimoRegistroRiesgoPsicosocial',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus(
          'ultimo_registro_riesgo_psicosocial_psicología'
        ),
        multiple: 17
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_ultimo_registro_riesgo_psicosocial',
            searchName: 'filtroUltimoRegistroRiesgoPsicosocial'
          },
          dataIndex: 'ultimo_registro_riesgo_psicosocial_psicología'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        month,
        filterRiesgoSeleccionado: riskParam,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getPsychosocialRiskPsychologyListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            ultima_valoración_psicologia: item?.ultima_valoración_psicologia
              ? formatDate(item.ultima_valoración_psicologia)
              : '',
            ultimo_registro_riesgo_psicosocial_psicología:
              item?.ultimo_registro_riesgo_psicosocial_psicología
                ? formatDate(item.ultimo_registro_riesgo_psicosocial_psicología)
                : ''
          };
        });
        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      additionalColumns={additionalColumns}
      onPaginationChange={getetData}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
PsychosocialRiskPsycologyListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getPsychosocialRiskPsychologyListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default PsychosocialRiskPsycologyListView;
